import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from "i18next";
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/QuickSetup.module.scss';
import { Icon, Input } from 'Components';
import RadioCard, {
	RadioCardOption
} from 'LearningManager/components/RadioCard';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import {
	ASSIGN_USERS_OPTION,
	CERT_EXPIRATION_BEFORE_START_ERROR,
	DUE_DATE_BEFORE_START_ERROR,
	isCertExpirationBeforeStart,
	isDueDateBeforeStart
} from 'LearningManager/constants/createAssignment.constant';

const certOptions: RadioCardOption[] = [
	{ value: 'one-year', label: 'learning.configure.options.oneYear' },
	{
		value: 'custom',
		label: 'learning.configure.options.customExpiration',
		showDatePicker: true,
		datePlaceholder: 'learning.configure.options.enterDeadlineDate'
	},
	{
		value: 'never',
		label: 'learning.configure.options.neverExpires'
	}
];

const startOptions: RadioCardOption[] = [
	{
		value: 'immediate',
		label: 'learning.configure.options.immediate'
	},
	{
		value: 'schedule',
		label: 'learning.configure.options.schedule',
		showDatePicker: true,
		datePlaceholder: 'learning.configure.options.enterStartDate'
	}
];

const dueOptions: RadioCardOption[] = [
	{
		value: 'none',
		label: 'learning.configure.options.none'
	},
	{
		value: 'custom',
		label: 'learning.configure.options.customExpiration',
		showDatePicker: true,
		datePlaceholder: 'learning.configure.options.enterDeadlineDate'
	}
];

const AssignTypeOptions: RadioCardOption[] = [
	{
		value: 'one-time-assignment',
		label: 'learning.configure.options.oneTimeAssignment',
		subLabel:
			'learning.configure.options.oneTimeAssignmentDescription'
	},
	{
		value: 'ongoing-assignment',
		label: 'learning.configure.options.ongoingAssignment',
		subLabel: 'learning.configure.options.ongoingAssignmentDescription'
	}
];

const Configure: React.FC = () => {
	const { t } = useTranslation();

	const {
		draft: {
			configure: { assignmentName }
		},
		updateConfigure
	} = useStepper();

	return (
		<div id="lms-ca-configure" className={styles.configure}>
			<Heading
				astrix
				title={t('learning.configure.name.title')}
				subHeading={t('learning.configure.name.subHeading')}
			/>
			<div className={styles.configure__input_container}>
				<Input
					id="lms-ca-configure-name"
					className={styles.configure__input}
					value={assignmentName}
					maxLength={70}
					onChange={(e) =>
						updateConfigure({ assignmentName: e.target.value })
					}
					placeholder={t('learning.configure.name.placeholder')}
				/>
				<span className={styles.configure__input_count}>{assignmentName.length}/70</span>
			</div>
			<Scheduling t={t} />
			<AssignmentRules t={t} />
		</div>
	);
};

const Scheduling = ({ t }: { t: TFunction }) => {
	const {
		draft: { configure },
		updateConfigure,
		isLimitedEdit
	} = useStepper();

	const {
		assignmentStart,
		assignmentStartDate,
		certDuration,
		certExpirationDate,
		dueDate,
		dueDateValue
	} = configure;

	const certExpirationError = isCertExpirationBeforeStart(
		configure,
		isLimitedEdit
	)
		? t(CERT_EXPIRATION_BEFORE_START_ERROR)
		: undefined;

	const dueDateError = isDueDateBeforeStart(configure, isLimitedEdit)
		? t(DUE_DATE_BEFORE_START_ERROR)
		: undefined;

	const onChange = (name: 'certDuration' | 'assignmentStart' | 'dueDate') => (
		value: string
	) => {
		updateConfigure({ [name]: value });
	};

	return (
		<div
			id="lms-ca-configure-scheduling"
			className={styles.configure__wrapper}
		>
			<Heading
				title={t('learning.configure.scheduling.title')}
				subHeading={t('learning.configure.scheduling.subHeading')}
			/>
			<div className={styles.configure__formContainer}>
				<RadioCard
					id="lms-ca-configure-cert-duration"
					title={'learning.configure.scheduling.certificationDuration.title'}					
					icon={<Icon type="durationIcon" role="img" />}
					tooltip={'learning.configure.scheduling.certificationDuration.tooltip'}
					required
					options={certOptions}
					value={certDuration}
					onChange={onChange('certDuration')}
					dateValue={certExpirationDate}
					onDateChange={(date) =>
						updateConfigure({ certExpirationDate: date })
					}
					error={certExpirationError}
				/>
				<RadioCard
					id="lms-ca-configure-assignment-start"
					title={'learning.configure.scheduling.assignmentStart.title'}
					icon={<Icon type="startIcon" role="img" />}
					tooltip={'learning.configure.scheduling.assignmentStart.tooltip'}
					required
					options={startOptions}
					value={assignmentStart}
					onChange={onChange('assignmentStart')}
					dateValue={assignmentStartDate}
					onDateChange={(date) =>
						updateConfigure({ assignmentStartDate: date })
					}
					disabled={isLimitedEdit}
					minSelectableDaysAhead={1}
				/>
				<RadioCard
					id="lms-ca-configure-due-date"
					title={'learning.configure.scheduling.dueDate.title'}
					icon={<Icon type="dueDateIcon" role="img" />}
					tooltip={'learning.configure.scheduling.dueDate.tooltip'}
					required
					options={dueOptions}
					value={dueDate}
					onChange={onChange('dueDate')}
					dateValue={dueDateValue}
					onDateChange={(date) =>
						updateConfigure({ dueDateValue: date })
					}
					error={dueDateError}
				/>
			</div>
		</div>
	);
};

const AssignmentRules = ({ t }: { t: TFunction }) => {
	const {
		draft: {
			configure: { assignmentType },
			customSetup: { selectedAssignUsersOptionId }
		},
		setupType,
		updateConfigure,
		isLimitedEdit
	} = useStepper();

	const isIndividualAudience =
		setupType === 'Custom Setup' &&
		selectedAssignUsersOptionId === ASSIGN_USERS_OPTION.INDIVIDUAL;

	useEffect(() => {
		if (
			isIndividualAudience &&
			!isLimitedEdit &&
			assignmentType === 'ongoing-assignment'
		) {
			updateConfigure({ assignmentType: 'one-time-assignment' });
		}
	}, [isIndividualAudience, isLimitedEdit, assignmentType]);

	const localizedAssignTypeOptions = AssignTypeOptions.map((option) => ({
		...option,
		disabled: option.value === 'ongoing-assignment' && isIndividualAudience
	}));

	return (
		<div id="lms-ca-configure-rules" className={styles.configure__wrapper}>
			<Heading
				title={t('learning.configure.assignmentRules.title')}
				subHeading={t('learning.configure.assignmentRules.subHeading')}
			/>
			<RadioCard
				id="lms-ca-configure-assignment-type"
				title={t('learning.configure.assignmentRules.assignmentType')}
				icon={<Icon type="assignTypeIcon" role="img" />}
				tooltip={t('learning.configure.assignmentRules.tooltip')}
				options={localizedAssignTypeOptions}
				required
				onChange={(value) => updateConfigure({ assignmentType: value })}
				value={assignmentType}
				disabled={isLimitedEdit}
			/>
		</div>
	);
};

export default Configure;